import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { CaretRight, Gauge, MagnifyingGlass, X } from '@phosphor-icons/react';
import { BottomNav, type AppTab } from './components/BottomNav';
const CompanyPanel = lazy(() => import('./components/CompanyPanel').then((module) => ({ default: module.CompanyPanel })));
const FinancePanel = lazy(() => import('./components/FinancePanel').then((module) => ({ default: module.FinancePanel })));
const FleetPanel = lazy(() => import('./components/FleetPanel').then((module) => ({ default: module.FleetPanel })));
import { Inspector } from './components/Inspector';
import { QuizModal } from './components/QuizModal';
import { RoomModal } from './components/RoomModal';
import { EffectBanner } from './components/EffectBanner';
import { CityInfoPanel, hasCityInfo } from './components/CityInfoPanel';
const RouteListPanel = lazy(() => import('./components/RouteListPanel').then((module) => ({ default: module.RouteListPanel })));
import { RoutePlanner } from './components/RoutePlanner';
import { AircraftDetailPanel } from './components/AircraftDetailPanel';
import { AuctionMinimizedFab, AuctionModal } from './components/AuctionModal';
import { AIRPORTS, airportById } from './data/airports';
import { TopBar } from './components/TopBar';
import { MobileAirportList } from './components/MobileAirportList';
import { WorldMap } from './components/WorldMap';
import { aircraftType } from './data/aircraft';
import { useGame } from './game/GameContext';
import type { GameEvent } from './game/types';
import type { StrategicAlertTarget } from './game/objectives';
import { QUIZ_QUESTIONS } from './data/quizQuestions';
import { buildFinancialSnapshot, type FinancialSnapshot } from './simulation/financialSnapshot';
import { buildPeriodReport, type PeriodReport } from './simulation/periodReports';
import { idleDeliveredAircraft } from './simulation/leasing';
import { distanceKm } from './simulation/math';
import { financialMoney, financialUnitFor, integer, money, percent } from './ui/format';
import { useI18n, type I18n } from './i18n';
import { groupAirportsForMobile } from './ui/airportGroups';
import { unlockedAirportIds } from './game/airportUnlocks';
import { useGuidedTutorial } from './hooks/useGuidedTutorial';
import { GuidedSkipConfirmModal } from './components/GuidedSkipConfirmModal';

function reportLabel(report: PeriodReport, t: I18n['t']) {
  return t(`app.report.${report.period}`);
}

function reportTheme(report: PeriodReport, t: I18n['t']) {
  return t(`app.reportTheme.${report.period}`);
}

function growthText(value: number | null) {
  if (value === null) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(1)}%`;
}

function shouldHideMapChrome() {
  if (typeof window === 'undefined') return false;
  const { innerWidth, innerHeight } = window;
  const portrait = innerHeight > innerWidth;
  const phoneLandscape = innerWidth > innerHeight && innerWidth <= 950 && innerHeight <= 560;
  return portrait || phoneLandscape;
}

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  const { innerWidth, innerHeight } = window;
  return innerWidth <= 820 || (innerHeight > innerWidth && innerWidth <= 1100);
}

const APP_TABS: AppTab[] = ['map', 'routes', 'fleet', 'finance', 'company'];

function tabFromHash(): AppTab {
  if (typeof window === 'undefined') return 'map';
  const tab = window.location.hash.slice(1).split('/')[0] as AppTab;
  return APP_TABS.includes(tab) ? tab : 'map';
}

function PeriodPopupCard({ report, snapshot }: { report: PeriodReport; snapshot: FinancialSnapshot }) {
  const { t } = useI18n();
  const totalCosts = report.operatingCosts + report.hubCosts + report.depreciation + report.interest;
  const headlineUnit = financialUnitFor([report.revenue, report.netProfit]);
  const incomeUnit = financialUnitFor([report.revenue, totalCosts, report.netProfit]);
  const cashFlowUnit = financialUnitFor([report.operatingProfit, report.interest + report.principalPaid, report.netCashFlow]);
  const balanceUnit = financialUnitFor([snapshot.totalAssets, snapshot.debt, snapshot.netAssets]);
  const netProfitMargin = report.revenue ? report.netProfit / report.revenue : 0;
  const revenueGrowth = report.comparisons.yearOverYear.revenue.change;
  const netProfitGrowth = report.comparisons.yearOverYear.netProfit.change;
  const netProfitMarginYoY = report.comparisons.yearOverYear.netProfitMargin.change;
  const netProfitMarginSeq = report.comparisons.sequential.netProfitMargin.change;
  const netCashFlowGrowth = report.comparisons.yearOverYear.netCashFlow.change;

  return (
    <article className="period-popup-card">
      <div className="period-popup-title">
        <span>{reportLabel(report, t)}</span>
        <strong>{t('ui.第 {start}-{end} 周', { start: report.weekStart, end: report.weekEnd })}</strong>
      </div>
      <div className="period-popup-hero">
        <div className="period-popup-revenue">
          <span>{t('ui.营业收入')}</span>
          <strong>{financialMoney(report.revenue, headlineUnit)}</strong>
          <small>{t('ui.{count} 名独立旅客', { count: integer(report.uniquePassengers) })} · {percent(report.averageLoadFactor, 1)} {t('ui.载客率')}</small>
          <small className={revenueGrowth !== null && revenueGrowth < 0 ? 'red' : 'green'}>{t('ui.同比增长')} {growthText(revenueGrowth)}</small>
        </div>
        <div className="period-popup-profit">
          <div>
            <span>{t('ui.净利润')}</span>
            <strong className={report.netProfit < 0 ? 'red' : 'green'}>{financialMoney(report.netProfit, headlineUnit)}</strong>
            <small className={netProfitGrowth !== null && netProfitGrowth < 0 ? 'red' : 'green'}>{t('ui.同比增长')} {growthText(netProfitGrowth)}</small>
          </div>
          <div>
            <span>{t('ui.净利润率')}</span>
            <strong className={netProfitMargin < 0 ? 'red' : 'green'}>{percent(netProfitMargin, 1)}</strong>
            <small className={netProfitMarginYoY !== null && netProfitMarginYoY < 0 ? 'red' : 'green'}>{t('ui.同比')} {growthText(netProfitMarginYoY)}</small>
            <small className={netProfitMarginSeq !== null && netProfitMarginSeq < 0 ? 'red' : 'green'}>{t('ui.环比')} {growthText(netProfitMarginSeq)}</small>
          </div>
        </div>
      </div>
      <div className="period-popup-statements">
        <section>
          <h3>{t('ui.利润表')}</h3>
          <div><span>{t('ui.收入')}</span><strong>{financialMoney(report.revenue, incomeUnit)}</strong></div>
          <div><span>{t('ui.成本与费用')}</span><strong className="red">−{financialMoney(totalCosts, incomeUnit)}</strong></div>
          <div><span>{t('ui.净利润')}</span><strong className={report.netProfit < 0 ? 'red' : 'green'}>{financialMoney(report.netProfit, incomeUnit)}</strong></div>
        </section>
        <section>
          <h3>{t('ui.现金流量表')}</h3>
          <div><span>EBITDA</span><strong>{financialMoney(report.operatingProfit, cashFlowUnit)}</strong></div>
          <div><span>{t('ui.还本付息')}</span><strong className="red">−{financialMoney(report.interest + report.principalPaid, cashFlowUnit)}</strong></div>
          <div><span>{t('ui.债务服务后现金流')}</span><strong className={report.netCashFlow < 0 ? 'red' : 'green'}>{financialMoney(report.netCashFlow, cashFlowUnit)}</strong></div>
          <div><span>{t('ui.同比增长')}</span><strong className={netCashFlowGrowth !== null && netCashFlowGrowth < 0 ? 'red' : 'green'}>{growthText(netCashFlowGrowth)}</strong></div>
        </section>
        <section>
          <h3>{t('ui.资产负债')}</h3>
          <div className="statement-parent"><span>总资产</span><strong>{financialMoney(snapshot.totalAssets, balanceUnit)}</strong></div>
          <div className="statement-child"><span>货币资金</span><strong>{financialMoney(snapshot.cash, balanceUnit)}</strong></div>
          <div className="statement-child"><span>固定资产-飞机</span><strong>{financialMoney(snapshot.aircraftAssets, balanceUnit)}</strong></div>
          <div className="statement-child"><span>固定资产-储油库</span><strong>{financialMoney(snapshot.fuelStorageAssets, balanceUnit)}</strong></div>
          <div className="statement-line statement-parent negative-line"><span>有息负债</span><strong className="red">−{financialMoney(snapshot.debt, balanceUnit)}</strong></div>
          <div className="statement-line grand-total"><span>所有者权益</span><strong className="green">{financialMoney(snapshot.netAssets, balanceUnit)}</strong></div>
        </section>
        <section>
          <h3>{t('ui.运营指标')}</h3>
          <div><span>{t('ui.周座位')}</span><strong>{integer(report.seats)}</strong></div>
          <div><span>{t('ui.未满足需求')}</span><strong>{integer(report.unmetDemand)}</strong></div>
          <div><span>{t('ui.燃油指数')}</span><strong>{report.fuelPriceIndex.toFixed(2)}</strong></div>
        </section>
      </div>
    </article>
  );
}

function PeriodReportModal({ reports, snapshot, onClose, onOpenFinance }: { reports: PeriodReport[]; snapshot: FinancialSnapshot; onClose: () => void; onOpenFinance: () => void }) {
  const { t } = useI18n();
  const primary = reports[0];
  const title = t('app.periodTitle', { start: primary.weekStart, end: primary.weekEnd, label: reportLabel(primary, t) });

  return (
    <div className="modal-backdrop report-backdrop" role="presentation" onClick={onClose}>
      <section className="period-report-sheet" role="dialog" aria-modal="true" aria-labelledby="period-report-title" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <header className="period-report-header">
          <div>
            <span className="eyebrow">{reportTheme(primary, t)}</span>
            <h2 id="period-report-title">{title}</h2>
            <p>{t('ui.本页汇总刚结束周期内的经营表现，可继续在财务中心查看完整明细和同比环比。')}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={t('ui.关闭')}><X weight="bold" size={22} /></button>
        </header>
        <div className="period-popup-grid">
          {reports.map((report) => <PeriodPopupCard key={report.period} report={report} snapshot={snapshot} />)}
        </div>
        <div className="period-popup-actions">
          <button className="secondary-action" onClick={onClose}>{t('ui.继续经营')}</button>
          <button className="primary-button" onClick={onOpenFinance}>{t('ui.查看财务中心')}</button>
        </div>
      </section>
    </div>
  );
}

function GameOverModal({ acquiredFor, message, onRestart }: { acquiredFor: number; message: string; onRestart: () => void }) {
  const { t } = useI18n();
  return (
    <div className="modal-backdrop game-over-backdrop" role="presentation">
      <section className="confirm-sheet game-over-sheet" role="dialog" aria-modal="true" aria-labelledby="game-over-title">
        <div className="sheet-handle" />
        <header className="confirm-header">
          <div>
            <span className="eyebrow">{t('ui.经营结束')}</span>
            <h2 id="game-over-title">{t('ui.公司被收购')}</h2>
            <p>{message}</p>
          </div>
        </header>
        <div className="confirm-danger-note">
          <strong>{t('ui.收购价格 {amount}', { amount: money(acquiredFor, false) })}</strong>
          <span>{t('ui.现金已归零，无法继续经营。点击“新开”重新开始。')}</span>
        </div>
        <div className="settlement-actions">
          <button className="primary-button danger" onClick={onRestart}>{t('ui.新开')}</button>
        </div>
      </section>
    </div>
  );
}

function EventModal({ events, onClose, onRouteSelect }: { events: GameEvent[]; onClose: () => void; onRouteSelect?: (routeId: string) => void }) {
  const { t } = useI18n();
  useEffect(() => {
    window.dispatchEvent(new Event('tutorial-blocking-modal-change'));
    return () => {
      window.dispatchEvent(new Event('tutorial-blocking-modal-change'));
    };
  }, []);

  return (
    <div className="modal-backdrop event-backdrop" role="presentation" data-tutorial-blocking-event-modal onClick={onClose}>
      <section className="event-sheet" role="dialog" aria-modal="true" aria-labelledby="event-title" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <header className="event-sheet-header">
          <div>
            <span className="eyebrow">{t('ui.经营提醒')}</span>
            <h2 id="event-title">{t('ui.本周发生了 {count} 件事', { count: events.length })}</h2>
            <p>{t('ui.这些事件已同步记录到财务中心的经营事件列表。')}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={t('ui.关闭')}><X weight="bold" size={22} /></button>
        </header>
        <div className="event-sheet-list">
          {events.map((event) => (
            <div className={`event-sheet-item ${event.tone}`} key={event.id}>
              <strong>{event.title}</strong>
              <p>{event.detail}</p>
              {(event.targetLabel || event.durationWeeks) && (
                <div className="event-sheet-meta">
                  {event.targetLabel && (
                    event.routeId && onRouteSelect
                      ? <button type="button" className="event-route-link" onClick={() => onRouteSelect(event.routeId!)} title={t('ui.查看航线')}>{event.targetLabel}</button>
                      : <span>{event.targetLabel}</span>
                  )}
                  {event.durationWeeks && <span>{t('ui.持续 {weeks} 周', { weeks: event.durationWeeks })}</span>}
                </div>
              )}
              {event.response && <p><b>{t('ui.可响应：')}</b>{event.response}</p>}
              {event.impact && (
                <div className="event-sheet-impact">
                  <span>{t('ui.停场成本')}</span>
                  <b className="red">{money(event.impact.cost, false)}</b>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="event-sheet-actions">
          <button className="primary-button" onClick={onClose}>{t('ui.继续经营')}</button>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const actions = useGame();
  const { t } = useI18n();
  const { game, loading } = actions;
  const [tab, setTab] = useState<AppTab>(tabFromHash);
  const [selectedAirportId, setSelectedAirportId] = useState<string>();
  const [focusAirportId, setFocusAirportId] = useState<string>();
  const [focusAirportNonce, setFocusAirportNonce] = useState(0);
  const [selectedRouteId, setSelectedRouteId] = useState<string>();
  const [selectedAiRouteId, setSelectedAiRouteId] = useState<string>();
  const [pendingRouteFocus, setPendingRouteFocus] = useState<{ originId: string; destinationId: string }>();
  const [routeOriginId, setRouteOriginId] = useState<string>();
  const [routeTargetId, setRouteTargetId] = useState<string>();
  const [routeAircraftId, setRouteAircraftId] = useState<string>();
  const [routeAirportFilter, setRouteAirportFilter] = useState<string>();
  const [routeFocusRequest, setRouteFocusRequest] = useState<{ routeId: string; requestId: number }>();
  const [selectedAircraftId, setSelectedAircraftId] = useState<string>();
  const [airportSearch, setAirportSearch] = useState('');
  const [searchCollapsed, setSearchCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('noflight-search-collapsed') === '1';
  });
  const [resetPending, setResetPending] = useState(false);
  const [showBorders, setShowBorders] = useState(true);
  const [performanceMode, setPerformanceMode] = useState(false);
  const [hideMapChrome, setHideMapChrome] = useState(shouldHideMapChrome);
  const [mobileViewport, setMobileViewport] = useState(isMobileViewport);
  const [mobileAirportListEnabled, setMobileAirportListEnabled] = useState(true);
  const [showCityInfo, setShowCityInfo] = useState(false);
  const [periodReports, setPeriodReports] = useState<PeriodReport[] | null>(null);
  const [pendingEvents, setPendingEvents] = useState<GameEvent[] | null>(null);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const previousWeek = useRef<number | null>(null);

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (!game) return;
    const before = previousWeek.current;
    previousWeek.current = game.week;
    if (before === null || game.week <= before) return;

    const newEvents = game.events.filter((event) => event.week === game.week);
    if (newEvents.length) setPendingEvents(newEvents);

    const dueReports: PeriodReport[] = [];
    if (game.week % 52 === 0) {
      const annual = buildPeriodReport(game.reports, 'year');
      if (annual) dueReports.push(annual);
    }
    if (game.week % 13 === 0) {
      const quarterly = buildPeriodReport(game.reports, 'quarter');
      if (quarterly) dueReports.push(quarterly);
    }
    if (dueReports.length && !newEvents.length) setPeriodReports(dueReports);
  }, [game]);

  useEffect(() => {
    if (!game || !routeOriginId) return;
    const idleIds = idleDeliveredAircraft(game).map((aircraft) => aircraft.id);
    if (!idleIds.length) {
      setRouteAircraftId(undefined);
      return;
    }
    if (!routeAircraftId || !idleIds.includes(routeAircraftId)) setRouteAircraftId(idleIds[0]);
  }, [game, routeAircraftId, routeOriginId]);

  useEffect(() => {
    const updateMapChrome = () => {
      setHideMapChrome(shouldHideMapChrome());
      setMobileViewport(isMobileViewport());
    };
    updateMapChrome();
    window.addEventListener('resize', updateMapChrome);
    return () => window.removeEventListener('resize', updateMapChrome);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('noflight-search-collapsed', searchCollapsed ? '1' : '0');
  }, [searchCollapsed]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (showCityInfo) {
        setShowCityInfo(false);
        return;
      }
      if (selectedAiRouteId) {
        setSelectedAiRouteId(undefined);
        return;
      }
      if (routeOriginId) {
        setRouteOriginId(undefined);
        setRouteTargetId(undefined);
        setRouteAircraftId(undefined);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [routeOriginId, selectedAiRouteId, showCityInfo]);

  useEffect(() => {
    if (!game || !pendingRouteFocus) return;
    const route = game.routes.find((item) => item.active
      && [item.originId, item.destinationId].sort().join('-') === [pendingRouteFocus.originId, pendingRouteFocus.destinationId].sort().join('-'));
    if (!route) return;
    setPendingRouteFocus(undefined);
    selectRoute(route.id);
  }, [game, pendingRouteFocus]);

  const availableAirportIds = useMemo(() => (game ? new Set(unlockedAirportIds(game.headquartersId, game.week)) : new Set<string>()), [game?.headquartersId, game?.week]);
  const availableAirports = useMemo(() => AIRPORTS.filter((airport) => availableAirportIds.has(airport.iata)), [availableAirportIds]);
  const mobileAirportGroups = useMemo(() => groupAirportsForMobile(game?.headquartersId ?? 'HKG', availableAirports), [availableAirports, game?.headquartersId]);
  const routeAirportIds = useMemo(() => {
    if (!routeOriginId) return new Set<string>();
    return new Set(
      game?.routes
        .filter((route) => route.active && (route.originId === routeOriginId || route.destinationId === routeOriginId))
        .flatMap((route) => [route.originId, route.destinationId])
        .filter((airportId) => airportId !== routeOriginId) ?? [],
    );
  }, [game?.routes, routeOriginId]);
  const routeAirportCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const route of game?.routes.filter((route) => route.active) ?? []) {
      counts.set(route.originId, (counts.get(route.originId) ?? 0) + 1);
      counts.set(route.destinationId, (counts.get(route.destinationId) ?? 0) + 1);
    }
    return counts;
  }, [game?.routes]);
  const hubAirportIds = useMemo(() => new Set(game?.hubs.map((hub) => hub.airportId) ?? []), [game?.hubs]);
  const mobileAirportListMode = mobileViewport && mobileAirportListEnabled && !!game;
  const reachableMobileAirportIds = useMemo(() => {
    if (!game || !routeOriginId) return undefined;
    const origin = airportById(routeOriginId);
    const idleAircraft = idleDeliveredAircraft(game);
    const planningAircraft = idleAircraft.find((aircraft) => aircraft.id === routeAircraftId) ?? idleAircraft[0];
    if (!planningAircraft) return new Set([routeOriginId]);
    const type = aircraftType(planningAircraft.typeId);
    return new Set(availableAirports
      .filter((airport) => airport.iata === routeOriginId || (
        type.runwayClass <= origin.runwayClass
        && type.runwayClass <= airport.runwayClass
        && distanceKm(origin, airport) <= type.rangeKm
      ))
      .map((airport) => airport.iata));
  }, [availableAirports, game, routeAircraftId, routeOriginId]);

  const guidedTutorial = useGuidedTutorial({ game, tab, mobileViewport, selectedAirportId, routeOriginId, routeTargetId });

  if (loading || !game) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">NF</div>
        <strong>{t('ui.正在读取航空公司账本')}</strong>
        <span>{t('ui.IndexedDB 本地存档')}</span>
      </div>
    );
  }

  function selectAirport(iata: string) {
    if (!game) return;
    setSelectedAiRouteId(undefined);
    if (routeOriginId && iata !== routeOriginId) {
      const existingRoute = game.routes.find((route) => route.active && (
        (route.originId === routeOriginId && route.destinationId === iata)
        || (route.destinationId === routeOriginId && route.originId === iata)
      ));
      if (existingRoute) {
        selectRoute(existingRoute.id);
        return;
      }
      setRouteTargetId(iata);
      setSelectedAirportId(iata);
      if (guidedTutorial.shouldShowCityInfoForAirport(iata)) setShowCityInfo(true);
      return;
    }
    setSelectedAirportId(iata);
    setSelectedRouteId(undefined);
    if (guidedTutorial.isGuidedRouteStep() && iata === 'HKG') setShowCityInfo(false);
    else if (hasCityInfo(iata)) setShowCityInfo(true);
  }

  function focusRouteForAircraft(aircraftId: string) {
    if (!game) return;
    const aircraft = game.aircraft.find((item) => item.id === aircraftId);
    const routeId = aircraft?.routeIds[0];
    if (!routeId) return;
    setRouteAirportFilter(undefined);
    setRouteFocusRequest((current) => ({ routeId, requestId: (current?.requestId ?? 0) + 1 }));
  }

  function selectRoute(routeId: string) {
    setSelectedRouteId(routeId);
    setSelectedAirportId(undefined);
    setSelectedAiRouteId(undefined);
    setRouteOriginId(undefined);
    setRouteTargetId(undefined);
    setRouteAircraftId(undefined);
    setShowCityInfo(false);
  }

  function selectAiRoute(managerId: string, routeId: string) {
    setSelectedAiRouteId(`${managerId}:${routeId}`);
    setSelectedRouteId(undefined);
    setSelectedAirportId(undefined);
    setRouteOriginId(undefined);
    setRouteTargetId(undefined);
    setRouteAircraftId(undefined);
    setShowCityInfo(false);
  }

  function changeTab(next: AppTab) {
    setTab(next);
    if (typeof window !== 'undefined') {
      window.location.hash = next === 'company'
        ? 'company/operations'
        : next === 'finance'
          ? 'finance/overview'
          : next;
    }
    if (next !== 'map') {
      setSelectedAiRouteId(undefined);
      setRouteOriginId(undefined);
      setRouteTargetId(undefined);
      setRouteAircraftId(undefined);
    }
    setSelectedAircraftId(undefined);
  }

  function reset() {
    setResetPending(true);
  }

  function confirmReset() {
    actions.resetGame();
    setSelectedAirportId(undefined);
    setSelectedRouteId(undefined);
    setSelectedAiRouteId(undefined);
    setSelectedAircraftId(undefined);
    setRouteOriginId(undefined);
    setRouteTargetId(undefined);
    setRouteAircraftId(undefined);
    changeTab('map');
    setResetPending(false);
    setPeriodReports(null);
  }

  function restart() {
    actions.resetGame();
    setSelectedAirportId(undefined);
    setSelectedRouteId(undefined);
    setSelectedAiRouteId(undefined);
    setSelectedAircraftId(undefined);
    setRouteOriginId(undefined);
    setRouteTargetId(undefined);
    setRouteAircraftId(undefined);
    changeTab('map');
    setPeriodReports(null);
  }

  const idleAircraftCount = idleDeliveredAircraft(game).length;
  const financialSnapshot = buildFinancialSnapshot(game);
  const resolvedAirport = selectedAirportId ? airportById(selectedAirportId) : undefined;
  const normalizedAirportSearch = airportSearch.trim().toLowerCase();
  const airportSearchResults = normalizedAirportSearch
    ? availableAirports
      .filter((airport) => [
        airport.iata,
        airport.nameZh,
        airport.nameEn,
        airport.country,
      ].some((value) => value.toLowerCase().includes(normalizedAirportSearch)))
      .slice(0, 7)
    : [];

  function beginRoute(originId: string) {
    if (!game) return;
    const firstIdleAircraft = idleDeliveredAircraft(game)[0];
    setRouteOriginId(originId);
    setRouteTargetId(undefined);
    setRouteAircraftId(firstIdleAircraft?.id);
  }

  function cancelRoutePlanning() {
    setRouteOriginId(undefined);
    setRouteTargetId(undefined);
    setRouteAircraftId(undefined);
  }

  function selectSearchAirport(iata: string) {
    setAirportSearch('');
    setFocusAirportId(iata);
    setFocusAirportNonce((nonce) => nonce + 1);
    selectAirport(iata);
  }

  return (
    <div className="app-shell">
      <TopBar
        game={game}
        onAdvance={actions.advanceWeek}
        room={actions.room}
        roomLocked={actions.roomLocked}
      />
      {actions.room && actions.room.phase !== 'lobby' && actions.roomLocked && (
        <button className="room-waiting-banner" onClick={() => setShowRoomModal(true)}>
          {actions.room.phase === 'processing'
            ? t('ui.所有玩家已提交，服务端正在结算本周…')
            : t('ui.已提交本周数据，等待其他玩家操作（{ready}/{total} 已提交）；当前为只读，可以查看但不能修改', { ready: actions.room.players.filter((player) => player.ready).length, total: actions.room.players.length })}
        </button>
      )}
      <EffectBanner game={game} onOpenResearch={actions.openResearchOpportunity} />
      <main className="app-main">
        <Suspense fallback={<div className="loading-panel">{t('ui.正在打开经营面板…')}</div>}>
        {tab === 'map' && (
          <div className="map-workspace" data-tutorial-map-workspace>
            {!mobileAirportListMode && (
              <WorldMap
                game={game}
                selectedAirportId={selectedAirportId}
                selectedRouteId={selectedRouteId}
                selectedAiRouteId={selectedAiRouteId}
                focusAirportId={focusAirportId}
                focusAirportNonce={focusAirportNonce}
                routeOriginId={routeOriginId}
                planningAircraftId={routeAircraftId}
                onAirportSelect={selectAirport}
                onRouteSelect={selectRoute}
                onAiRouteSelect={selectAiRoute}
                showBorders={showBorders}
                performanceMode={performanceMode}
                availableAirportIds={availableAirportIds}
              />
            )}
            <MobileAirportList
              groups={mobileAirportGroups}
              listMode={mobileAirportListMode}
              selectedAirportId={selectedAirportId}
              routeOriginId={mobileAirportListMode ? routeOriginId : undefined}
              reachableAirportIds={mobileAirportListMode ? reachableMobileAirportIds : undefined}
              routeAirportIds={mobileAirportListMode ? routeAirportIds : undefined}
              routeAirportCounts={mobileAirportListMode ? routeAirportCounts : undefined}
              hubAirportIds={mobileAirportListMode ? hubAirportIds : undefined}
              onAirportSelect={selectAirport}
              onToggleListMode={() => setMobileAirportListEnabled((enabled) => !enabled)}
            />
            {!hideMapChrome && (
              <>
                <div className="map-option-toggles" aria-label={t('ui.地图显示设置')}>
                  <button
                    className={`border-toggle ${showBorders ? 'on' : 'off'}`}
                    onClick={() => setShowBorders((current) => !current)}
                    aria-pressed={showBorders}
                    title={showBorders ? t('ui.隐藏国界') : t('ui.显示国界')}
                    aria-label={showBorders ? t('ui.隐藏国界') : t('ui.显示国界')}
                  >
                    <i className="border-toggle-icon" />
                    <span className="border-toggle-text">{t('ui.国界')}</span>
                    <b>{showBorders ? t('ui.开') : t('ui.关')}</b>
                  </button>
                  <button
                    className={`performance-toggle ${performanceMode ? 'on' : 'off'}`}
                    onClick={() => setPerformanceMode((current) => !current)}
                    aria-pressed={performanceMode}
                    title={performanceMode ? t('ui.关闭性能模式') : t('ui.开启性能模式')}
                    aria-label={performanceMode ? t('ui.关闭性能模式') : t('ui.开启性能模式')}
                  >
                    <Gauge weight="bold" size={15} />
                    <span>{t('ui.性能')}</span>
                    <b>{performanceMode ? t('ui.开') : t('ui.关')}</b>
                  </button>
                </div>
                {searchCollapsed ? (
                  <button
                    type="button"
                    className="airport-search-toggle"
                    aria-label={t('ui.展开机场搜索')}
                    onClick={() => setSearchCollapsed(false)}
                  >
                    <MagnifyingGlass weight="bold" size={20} />
                  </button>
                ) : (
                  <div className="airport-search-box">
                    <label>
                      <span>{t('ui.搜索机场')}</span>
                      <input
                        aria-label={t('ui.搜索机场')}
                        value={airportSearch}
                        onChange={(event) => setAirportSearch(event.target.value)}
                        placeholder={t('ui.城市 / 机场 / IATA')}
                      />
                      <button
                        type="button"
                        className="airport-search-close"
                        aria-label={t('ui.收起搜索')}
                        onClick={() => setSearchCollapsed(true)}
                      >
                        <X weight="bold" size={14} />
                      </button>
                    </label>
                    {airportSearchResults.length > 0 && (
                      <div className="airport-search-results">
                        {airportSearchResults.map((airport) => (
                          <button key={airport.iata} onClick={() => selectSearchAirport(airport.iata)}>
                            <strong>{airport.nameZh}</strong>
                            <span>{airport.iata} · {airport.region}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {resolvedAirport && showCityInfo && (
              <CityInfoPanel
                airport={resolvedAirport}
                game={game}
                onClose={() => setShowCityInfo(false)}
              />
            )}
            <Inspector
              game={game}
              selectedAirportId={selectedAirportId}
              selectedRouteId={selectedRouteId}
              selectedAiRouteId={selectedAiRouteId}
              routeOriginId={routeOriginId}
              routeAircraftId={routeAircraftId}
              onBeginRoute={beginRoute}
              onCancelRoute={cancelRoutePlanning}
              onRouteAircraftChange={setRouteAircraftId}
              onBuildHub={actions.buildHub}
              onViewRoutes={(airportId) => {
                setRouteAirportFilter(airportId);
                setRouteOriginId(undefined);
                setRouteTargetId(undefined);
                setRouteAircraftId(undefined);
                changeTab('routes');
              }}
              onCloseRoute={(routeId) => { actions.closeRoute(routeId); setSelectedRouteId(undefined); }}
              onAssignAircraft={actions.assignAircraft}
              onUnassignAircraft={actions.unassignAircraft}
              onSetRouteFareRatio={actions.setRouteFareRatio}
              onViewAircraft={setSelectedAircraftId}
              onAirportSelect={selectAirport}
              onRouteSelect={selectRoute}
              onAiRouteSelect={selectAiRoute}
              onOpenRoute={(originId, destinationId, aircraftId) => {
                setPendingRouteFocus({ originId, destinationId });
                actions.openRoute(originId, destinationId, aircraftId);
                setSelectedAiRouteId(undefined);
              }}
            />
            {!game.aircraft.length && (
              <button className="first-step-card" onClick={() => changeTab('fleet')}>
                <span>{t('ui.第一步')}</span>
                <strong>{t('ui.购买一架飞机')}</strong>
                <p>{t('ui.比较全款、贷款和二手方案')}</p>
                <b>{t('ui.前往采购')} <CaretRight weight="bold" size={12} /></b>
              </button>
            )}
          </div>
        )}
        {tab === 'routes' && (
          <RouteListPanel
            game={game}
            airportFilter={routeAirportFilter}
            onAirportFilterChange={setRouteAirportFilter}
            onAssignAircraft={actions.assignAircraft}
            onUnassignAircraft={actions.unassignAircraft}
            onSetRouteFareRatio={actions.setRouteFareRatio}
            onRunLowMarginScan={actions.runLowMarginScan}
            onViewAircraft={setSelectedAircraftId}
            routeFocusRequest={routeFocusRequest}
          />
        )}
        {tab === 'fleet' && <FleetPanel game={game} onBuy={actions.buyAircraft} onLease={actions.leaseAircraft} onSell={actions.sellAircraft} onReturnLease={actions.returnLeasedAircraft} onMaintain={actions.maintainAircraft} onMaintainLowCondition={actions.maintainLowConditionAircraft} onSetSpeedModifier={actions.setAircraftSpeedModifier} onSetCabinConfig={actions.setAircraftCabinConfig} onSetCrewSalaryModifier={actions.setAircraftCrewSalaryModifier} onViewAircraft={setSelectedAircraftId} onPurchaseDialogChange={guidedTutorial.onPurchaseDialogChange} />}
        {tab === 'finance' && <FinancePanel game={game} onReset={reset} onPayOffLoan={actions.payOffLoan} onPayDownBankLoan={actions.payDownBankLoan} onRefinanceLoan={actions.refinanceLoan} onExtendBankLoan={actions.extendBankLoan} onBorrowBankLoan={actions.borrowBankLoan} onRouteSelect={(routeId) => { selectRoute(routeId); changeTab('map'); }} onViewAircraft={setSelectedAircraftId} onRespondToMarketEvent={actions.respondToMarketEvent} />}
        {tab === 'company' && <CompanyPanel game={game} guidedTutorialEnabled={guidedTutorial.tutorialEnabled} onGuidedTutorialChange={guidedTutorial.setTutorialEnabled} onRecruitAssistant={actions.recruitAssistant} onDismissAssistant={actions.dismissAssistant} onCompleteAudit={actions.completeAudit} onApplyForListing={actions.applyForListing} onCompleteListing={actions.completeListing} onIssueShares={actions.issueShares} onBuybackShares={actions.buybackShares} onPlayerSellShares={actions.playerSellShares} onPlayerBuyShares={actions.playerBuyShares} onPlayerSellCompanyShares={actions.playerSellCompanyShares} onPlayerBuyCompanyShares={actions.playerBuyCompanyShares} onDeclareDividend={actions.declarePlayerDividend} onStartPlayerVentureRound={actions.startPlayerVentureRound} onInvestVentureRound={actions.investInVentureRound} onBuyFuel={actions.buyFuel} onBuyFuelFuture={actions.buyFuelFuture} onShortFuelFuture={actions.shortFuelFuture} onSellFuel={actions.sellFuel} onSellFuelFuture={actions.sellFuelFuture} onBuildFuelStorage={actions.buildFuelStorage} onCancelFuelStorageQueue={actions.cancelFuelStorageQueue} onExtendFuelStorageFacility={actions.extendFuelStorageFacility} onSetAllCrewSalaryModifier={actions.setAllAircraftCrewSalaryModifier} onApplyEngineeringStrategy={actions.applyEngineeringStrategy} onUpgradeHub={actions.upgradeHub} onSetHubSpecialization={actions.setHubSpecialization} onAddAiManagers={actions.addAiManagers} onAddAdvancedAiManager={actions.addAdvancedAiManager} onAddStrategicAiManager={actions.addStrategicAiManager} onAddEliteAiManager={actions.addEliteAiManager} onRenameCompany={actions.renameCompany} onQuizModeChange={actions.setQuizModeEnabled} onOpenRoom={() => setShowRoomModal(true)} onAlert={(target: StrategicAlertTarget) => {
          if (target.kind === 'route') { selectRoute(target.routeId); changeTab('map'); }
          else if (target.kind === 'aircraft') { setSelectedAircraftId(target.aircraftId); }
          else if (target.kind === 'finance') {
            changeTab('finance');
            window.location.hash = `finance/${target.tab}`;
          } else {
            changeTab('company');
            window.location.hash = `company/${target.section}`;
          }
        }} roomId={actions.room?.id} />}
        </Suspense>
      </main>
      <BottomNav active={tab} badges={{ fleet: idleAircraftCount }} onChange={changeTab} />
      {guidedTutorial.skipConfirmationOpen && (
        <GuidedSkipConfirmModal
          onCancel={guidedTutorial.cancelSkip}
          onConfirm={guidedTutorial.confirmSkip}
        />
      )}
      {routeOriginId && routeTargetId && guidedTutorial.routePlannerReady && (
        <RoutePlanner
          game={game}
          originId={routeOriginId}
          destinationId={routeTargetId}
          preferredAircraftId={routeAircraftId}
          onClose={() => setRouteTargetId(undefined)}
          onConfirm={(aircraftId) => {
            actions.openRoute(routeOriginId, routeTargetId, aircraftId);
            setRouteOriginId(undefined);
            setRouteTargetId(undefined);
            setRouteAircraftId(undefined);
            setSelectedAirportId(routeTargetId);
          }}
        />
      )}
      {resetPending && (
        <div className="modal-backdrop" role="presentation" onClick={() => setResetPending(false)}>
          <section className="confirm-sheet" role="dialog" aria-modal="true" aria-labelledby="reset-confirm-title" onClick={(event) => event.stopPropagation()}>
            <div className="sheet-handle" />
            <header className="confirm-header">
              <div>
                <span className="eyebrow">{t('ui.重置公司')}</span>
                <h2 id="reset-confirm-title">{t('ui.清除当前公司和所有经营记录？')}</h2>
                <p>{t('ui.此操作无法撤销，本地存档会回到初始状态。')}</p>
              </div>
              <button className="icon-button" onClick={() => setResetPending(false)} aria-label={t('ui.关闭')}>×</button>
            </header>
            <div className="confirm-danger-note">
              <strong>{t('ui.将删除当前现金、机队、航线、贷款和流水记录。')}</strong>
              <span>{t('ui.确认后会立即回到地图初始视图。')}</span>
            </div>
            <div className="settlement-actions">
              <button className="secondary-action" onClick={() => setResetPending(false)}>{t('ui.取消')}</button>
              <button className="primary-button danger" onClick={confirmReset}>{t('ui.确认重置')}</button>
            </div>
          </section>
        </div>
      )}
      {periodReports && (
        <PeriodReportModal
          reports={periodReports}
          snapshot={financialSnapshot}
          onClose={() => setPeriodReports(null)}
          onOpenFinance={() => {
            setPeriodReports(null);
            changeTab('finance');
          }}
        />
      )}
      {pendingEvents && (
        <EventModal
          events={pendingEvents}
          onClose={() => {
            setPendingEvents(null);
            if (game && (game.week % 52 === 0 || game.week % 13 === 0)) {
              const dueReports: PeriodReport[] = [];
              if (game.week % 52 === 0) {
                const annual = buildPeriodReport(game.reports, 'year');
                if (annual) dueReports.push(annual);
              }
              if (game.week % 13 === 0) {
                const quarterly = buildPeriodReport(game.reports, 'quarter');
                if (quarterly) dueReports.push(quarterly);
              }
              if (dueReports.length) setPeriodReports(dueReports);
            }
          }}
          onRouteSelect={(routeId) => {
            setPendingEvents(null);
            selectRoute(routeId);
            changeTab('map');
          }}
        />
      )}
      {game.gameOver && (
        <GameOverModal
          acquiredFor={game.gameOver.acquiredFor}
          message={game.gameOver.message}
          onRestart={restart}
        />
      )}
      {actions.notice && (
        <button className="toast" onClick={actions.clearNotice}>
          <span>!</span>
          <strong>{actions.notice}</strong>
          <X weight="bold" size={14} />
        </button>
      )}
      {(showRoomModal || actions.room?.phase === 'lobby') && (
        <RoomModal
          room={actions.room}
          onCreate={actions.createRoom}
          onJoin={actions.joinRoom}
          onLeave={actions.leaveRoom}
          onUpdateSettings={actions.updateRoomSettings}
          onChooseCity={actions.chooseCity}
          onSetReady={actions.setRoomReady}
          onStartGame={actions.startRoomGame}
          onClose={() => setShowRoomModal(false)}
        />
      )}
      {selectedAircraftId && (
        <AircraftDetailPanel
          game={game}
          aircraftId={selectedAircraftId}
          onClose={() => setSelectedAircraftId(undefined)}
          onMaintain={actions.maintainAircraft}
          onSell={actions.sellAircraft}
          onReturnLease={actions.returnLeasedAircraft}
          onUnassignAircraft={actions.unassignAircraft}
          onReplaceAircraft={(routeId, fromAircraftId, toAircraftId) => {
            focusRouteForAircraft(fromAircraftId);
            actions.replaceAircraftOnRoute(routeId, fromAircraftId, toAircraftId);
          }}
          onSetSpeedModifier={(aircraftId, modifier) => {
            focusRouteForAircraft(aircraftId);
            actions.setAircraftSpeedModifier(aircraftId, modifier);
          }}
          onSetFareRatio={(aircraftId, fareRatio) => {
            focusRouteForAircraft(aircraftId);
            actions.setAircraftFareRatio(aircraftId, fareRatio);
          }}
          onSetCabinConfig={(aircraftId, cabinConfig) => {
            focusRouteForAircraft(aircraftId);
            actions.setAircraftCabinConfig(aircraftId, cabinConfig);
          }}
          onSetCrewSalaryModifier={(aircraftId, modifier) => {
            focusRouteForAircraft(aircraftId);
            actions.setAircraftCrewSalaryModifier(aircraftId, modifier);
          }}
        />
      )}
      {game?.quizModeEnabled && game?.researchOpportunity?.status === 'available' && (
        <QuizModal
          question={QUIZ_QUESTIONS.find((question) => question.id === game.researchOpportunity?.questionId) ?? QUIZ_QUESTIONS[0]}
          onResolve={(answerIndex) => actions.resolveResearch(answerIndex)}
          onSnooze={actions.snoozeResearchOpportunity}
          onDismiss={actions.dismissResearchOpportunity}
          onBuildHub={() => changeTab('map')}
          onBuyAircraft={() => changeTab('fleet')}
          hasHubVoucher={game.vouchers?.hubDiscount70}
          hasAircraftVoucher={game.vouchers?.aircraftDiscount70}
        />
      )}
      {game.activeAuction && !game.activeAuction.minimized && (
        <AuctionModal
          game={game}
          onBid={actions.placeAuctionBid}
          onFixedBid={actions.placeFixedAuctionBid}
          onAbandon={actions.abandonAuction}
          onMinimize={() => actions.minimizeAuction(true)}
        />
      )}
      <AuctionMinimizedFab game={game} onRestore={() => actions.minimizeAuction(false)} />
    </div>
  );
}
